/**
 * Stage History Service
 *
 * PE-769: Tracks opportunity stage progression with timing and history.
 * Provides visual pipeline data and stage advancement functionality.
 */

import { createPersistedMockData } from './mockDataManager';
import { logStatusChanged } from './activityLogService';

export interface StageChange {
  id: string;
  opportunityId: string;
  fromStage: string;
  toStage: string;
  duration: number; // milliseconds in previous stage
  userId: string;
  userName: string;
  timestamp: string;
  notes?: string;
}

export interface OpportunityStage {
  id: string;
  name: string;
  label: string;
  order: number;
  color: string;
  isClosedWon: boolean;
  isClosedLost: boolean;
  probability: number; // 0-100
}

// Predefined opportunity stages with Regal's probability mapping (PE-1459)
export const OPPORTUNITY_STAGES: OpportunityStage[] = [
  {
    id: 'meeting_set',
    name: 'Meeting Set',
    label: 'Meeting Set',
    order: 1,
    color: 'gray',
    isClosedWon: false,
    isClosedLost: false,
    probability: 10,
  },
  {
    id: 'discovery',
    name: 'Discovery',
    label: 'Discovery',
    order: 2,
    color: 'blue',
    isClosedWon: false,
    isClosedLost: false,
    probability: 20,
  },
  {
    id: 'pitch',
    name: 'Pitch',
    label: 'Pitch',
    order: 3,
    color: 'purple',
    isClosedWon: false,
    isClosedLost: false,
    probability: 40,
  },
  {
    id: 'evaluation',
    name: 'Evaluation',
    label: 'Evaluation',
    order: 4,
    color: 'orange',
    isClosedWon: false,
    isClosedLost: false,
    probability: 60,
  },
  {
    id: 'pending_deployment',
    name: 'Pending Deployment',
    label: 'Pending Deployment',
    order: 5,
    color: 'teal',
    isClosedWon: false,
    isClosedLost: false,
    probability: 80,
  },
  {
    id: 'closed_won',
    name: 'Closed Won',
    label: 'Closed Won',
    order: 6,
    color: 'green',
    isClosedWon: true,
    isClosedLost: false,
    probability: 100,
  },
  {
    id: 'closed_lost',
    name: 'Closed Lost',
    label: 'Closed Lost',
    order: 7,
    color: 'red',
    isClosedWon: false,
    isClosedLost: true,
    probability: 0,
  },
];

// Storage using persistence pattern
export const persistedStageHistory = createPersistedMockData<StageChange>(
  'crm_stage_history',
  []
);

/**
 * Get stage history for an opportunity
 */
export const getStageHistory = (opportunityId: string): StageChange[] => {
  const allHistory = persistedStageHistory.get();
  return allHistory
    .filter(change => change.opportunityId === opportunityId)
    .sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
};

/**
 * Get current stage duration
 */
export const getCurrentStageDuration = (opportunityId: string): number => {
  const history = getStageHistory(opportunityId);
  if (history.length === 0) return 0;

  const lastChange = history[history.length - 1];
  const now = new Date().getTime();
  const changeTime = new Date(lastChange.timestamp).getTime();

  return now - changeTime;
};

/**
 * Get time spent in each stage
 */
export const getTimeInEachStage = (opportunityId: string): Record<string, number> => {
  const history = getStageHistory(opportunityId);
  const timeByStage: Record<string, number> = {};

  history.forEach(change => {
    if (!timeByStage[change.toStage]) {
      timeByStage[change.toStage] = 0;
    }
    timeByStage[change.toStage] += change.duration;
  });

  // Add current stage duration
  if (history.length > 0) {
    const lastStage = history[history.length - 1].toStage;
    const currentDuration = getCurrentStageDuration(opportunityId);
    timeByStage[lastStage] = (timeByStage[lastStage] || 0) + currentDuration;
  }

  return timeByStage;
};

/**
 * Log a stage change
 */
export const logStageChange = (
  opportunityId: string,
  fromStage: string,
  toStage: string,
  userId: string,
  userName: string,
  notes?: string
): StageChange => {
  const history = getStageHistory(opportunityId);

  // Calculate duration in previous stage
  let duration = 0;
  if (history.length > 0) {
    const lastChange = history[history.length - 1];
    const now = new Date().getTime();
    const lastChangeTime = new Date(lastChange.timestamp).getTime();
    duration = now - lastChangeTime;
  }

  const change: StageChange = {
    id: `stage_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    opportunityId,
    fromStage,
    toStage,
    duration,
    userId,
    userName,
    timestamp: new Date().toISOString(),
    notes,
  };

  const allHistory = persistedStageHistory.get();
  persistedStageHistory.set([...allHistory, change]);

  // Also log to activity timeline
  logStatusChanged(
    opportunityId,
    'Opportunity__c',
    fromStage,
    toStage,
    userId,
    userName
  );

  return change;
};

/**
 * Get stage by ID
 */
export const getStageById = (stageId: string): OpportunityStage | undefined => {
  return OPPORTUNITY_STAGES.find(s => s.id === stageId);
};

/**
 * Get stage by name
 */
export const getStageByName = (stageName: string): OpportunityStage | undefined => {
  return OPPORTUNITY_STAGES.find(
    s => s.name.toLowerCase() === stageName.toLowerCase()
  );
};

/**
 * Get next stage in the pipeline
 */
export const getNextStage = (currentStageId: string): OpportunityStage | null => {
  const current = getStageById(currentStageId);
  if (!current) return null;

  // Filter out Closed Lost from normal progression
  const normalStages = OPPORTUNITY_STAGES.filter(s => !s.isClosedLost);
  const currentIndex = normalStages.findIndex(s => s.id === currentStageId);

  if (currentIndex === -1 || currentIndex >= normalStages.length - 1) {
    return null;
  }

  return normalStages[currentIndex + 1];
};

/**
 * Get previous stage in the pipeline
 */
export const getPreviousStage = (currentStageId: string): OpportunityStage | null => {
  const current = getStageById(currentStageId);
  if (!current) return null;

  const normalStages = OPPORTUNITY_STAGES.filter(s => !s.isClosedLost);
  const currentIndex = normalStages.findIndex(s => s.id === currentStageId);

  if (currentIndex <= 0) {
    return null;
  }

  return normalStages[currentIndex - 1];
};

/**
 * Format duration for display
 */
export const formatDuration = (ms: number): string => {
  if (ms < 0) return '0 secs';

  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days} day${days > 1 ? 's' : ''}`;
  if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
  if (minutes > 0) return `${minutes} min${minutes > 1 ? 's' : ''}`;
  return `${seconds} sec${seconds !== 1 ? 's' : ''}`;
};

/**
 * Get stage index for comparison
 */
export const getStageIndex = (stageId: string): number => {
  const normalStages = OPPORTUNITY_STAGES.filter(s => !s.isClosedLost);
  return normalStages.findIndex(s => s.id === stageId);
};

/**
 * Check if a stage is completed (before current)
 */
export const isStageCompleted = (
  stageId: string,
  currentStageId: string
): boolean => {
  const stageIndex = getStageIndex(stageId);
  const currentIndex = getStageIndex(currentStageId);
  return stageIndex !== -1 && currentIndex !== -1 && stageIndex < currentIndex;
};

/**
 * Map display stage name to stage ID
 */
export const mapStageNameToId = (stageName: string): string => {
  // Handle common stage name formats
  const normalized = stageName.toLowerCase().replace(/\s+/g, '_');
  const stage = OPPORTUNITY_STAGES.find(
    s =>
      s.id === normalized ||
      s.name.toLowerCase() === stageName.toLowerCase() ||
      s.label.toLowerCase() === stageName.toLowerCase()
  );
  return stage?.id || 'prospecting';
};

/**
 * Get all stage history (for debugging)
 */
export const getAllStageHistory = (): StageChange[] => {
  return persistedStageHistory.get();
};

/**
 * Clear stage history for an opportunity
 */
export const clearStageHistory = (opportunityId: string): void => {
  const allHistory = persistedStageHistory.get();
  const filtered = allHistory.filter(
    change => change.opportunityId !== opportunityId
  );
  persistedStageHistory.set(filtered);
};
